/**
 * The grant payload rules, client side (SPEC §10, §7.1).
 *
 * §10 names two payloads the server refuses: `all` carrying `report_slugs`,
 * and `selected` with an empty list. Both are almost certainly a UI bug rather
 * than an intent, so the editor is arranged not to produce either — the first
 * is impossible by construction (`toGrantInputs` drops the slugs), the second
 * blocks the save button.
 *
 * **The server is still the contract.** These are here so an administrator
 * finds out at the checkbox rather than at the 422, not so the server can stop
 * checking. Both rejections are asserted against the API in
 * `api/tests/test_admin_api.py`.
 */

import type { GrantInput, GrantScope } from './adminTypes'

export interface DraftGrant {
  readonly department: string
  readonly scope: GrantScope
  readonly slugs: readonly string[]
}

/** One message per offending row, keyed by its index. Empty means submittable. */
export function draftProblems(draft: readonly DraftGrant[]): Record<number, string> {
  const problems: Record<number, string> = {}
  draft.forEach((grant, index) => {
    if (grant.scope === 'selected' && grant.slugs.length === 0) {
      problems[index] = 'Pick at least one report, or remove this department.'
    }
  })
  return problems
}

/**
 * The wire payload.
 *
 * An `all` grant sends no slugs even if the draft is carrying some from before
 * the radio was switched — the server refuses the combination, and it is right
 * to: "every report, but only these" has no meaning.
 */
export function toGrantInputs(draft: readonly DraftGrant[]): GrantInput[] {
  return draft.map((grant) => ({
    department: grant.department,
    scope: grant.scope,
    report_slugs: grant.scope === 'all' ? [] : [...grant.slugs],
  }))
}
